// 2026-05-26 - FormuLearning - 프로젝트 선택 모달 (MOD-PROJECT-LOAD)
// 2026-09-09 수정 - 이슈 #72(M901-05): **로드 성공·취소를 알린다.**
//   좌측 패널에서 프로젝트를 클릭해도 곧바로 열리지 않고 이 모달이 뜬다. Cancel·✕·
//   backdrop 으로 빠져나오면 화면은 **이전 프로젝트 그대로**인데, 모달에 다른 이름이
//   크게 떴다 사라지므로 그쪽으로 갔다고 믿기 쉽다. 그 착각 위에서 Step 1 을 재검색하면
//   엉뚱한 프로젝트가 덮인다(260901 신고 건의 실제 형상 — 등록부 M901 §2.1).
//   **아무 일도 일어나지 않았다는 사실 자체를 알리는 자리**가 지금까지 없었다.
// 2026-09-21 수정 - 이슈 #77(G6): API Name → Description(memo, 3줄 말줄임) · Last Updated → Last Modified
//   (내부데이터 최종 수정일, BR-PRJ-03).
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업이 있으면 Load 를 막는다(방어 — 진입점 `LeftPanel.openProject` 가 이미 막는다).
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 스텝 명칭 → `lib/steps`.
// 2026-11-03 수정 - 이슈 #114: viewer 는 Hide/Unhide·Delete 비활성.
// 파일 위치: frontend/src/features/project/ProjectLoadModal.tsx

import { useState } from 'react'
import Modal from '../../components/feedback/Modal'
import { useProjectStore } from '../../stores/projectStore'
import { useUiStore } from '../../stores/uiStore'
import { useSaveBuffer } from '../../stores/saveBufferStore'
import { recordOperation } from '../../api/operations'
import { toast } from '../../stores/toastStore'
import { blockIfBusy } from '../../stores/busyStore'
import { STEP_LABELS } from '../../lib/steps'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'


const STATUS_COLORS: Record<string, string> = {
  in_progress: 'var(--color-accent-purple)',
  completed: 'var(--color-accent-teal)',
}

export default function ProjectLoadModal() {
  const { activeModal, closeModal, selectedProjectId } = useUiStore()
  const { projects, loadProject, currentProject, setHidden, removeProject } = useProjectStore()
  const canWrite = useCanWrite()
  const [loading, setLoading] = useState(false)

  const project = projects.find((p) => p.id === selectedProjectId)
  const isCurrentProject = currentProject?.id === selectedProjectId

  // 로드하지 않고 닫힌 경로 전부(Cancel·✕·backdrop·Esc)가 여기를 지난다.
  // 이미 열려 있던 프로젝트를 다시 고른 경우엔 알리지 않는다 — 바뀐 것이 없다.
  const handleDismiss = () => {
    if (!isCurrentProject) {
      toast(
        currentProject
          ? `프로젝트 로드를 취소했습니다. 현재 프로젝트는 「${currentProject.name}」 입니다.`
          : '프로젝트 로드를 취소했습니다. 열린 프로젝트가 없습니다.',
        'warn',
      )
    }
    closeModal()
  }

  // 2026-07-09 - 소프트 삭제 (이슈 #35): 실수 방지를 위해 먼저 Hide 한 뒤 Unhide 옆에서만 삭제.
  const handleDelete = async () => {
    if (!selectedProjectId) return
    if (!window.confirm('Are you sure you want to delete this project?')) return
    await removeProject(selectedProjectId)
    closeModal()
  }

  const handleLoad = async () => {
    if (!selectedProjectId) return
    if (blockIfBusy()) return
    // 2026-07-09 - 이슈 #33 4단계: Autosave OFF 미저장 편집이 있으면 전환 전 경고.
    if (useSaveBuffer.getState().hasDirty()) {
      const ok = window.confirm('미저장 편집이 있습니다. 저장하지 않고 다른 프로젝트를 열까요?')
      if (!ok) return
    }
    setLoading(true)
    try {
      await loadProject(selectedProjectId)
      useSaveBuffer.getState().clearDirty()
      recordOperation('project_load', selectedProjectId)
      // `project` 는 아래 `if (!project) return null` 로 좁혀지지만 그 가드가 이 콜백
      // 정의보다 뒤라 타입이 좁혀지지 않는다. 스토어에서 확정 이름을 다시 읽는다.
      const loaded = useProjectStore.getState().currentProject
      toast(`「${loaded?.name ?? '선택한'}」 프로젝트를 로드했습니다.`)
      closeModal()
    } finally {
      setLoading(false)
    }
  }

  if (!project) return null

  const stepLabel = project.current_step >= 1 && project.current_step <= 6
    ? STEP_LABELS[project.current_step - 1]
    : '—'
  const updatedAt = new Date(project.data_updated_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })

  return (
    <Modal open={activeModal === 'project-load'} onClose={handleDismiss} title="Load Project">
      <div className="space-y-3">
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-text-primary">{project.name}</span>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ background: project.status === 'completed' ? 'var(--bg-chip-teal)' : 'var(--bg-chip-purple)', color: STATUS_COLORS[project.status] }}
            >
              {project.status === 'completed' ? 'COMPLETED' : 'IN PROGRESS'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-y-2 text-[11px]">
            <div className="col-span-2">
              <span className="text-text-muted">Description</span>
              <p className="line-clamp-3 whitespace-pre-wrap break-words font-medium text-text-body" title={project.memo ?? undefined}>
                {project.memo || '—'}
              </p>
            </div>
            <div>
              <span className="text-text-muted">Current Step</span>
              <p className="font-medium text-text-body">Step {project.current_step} · {stepLabel}</p>
            </div>
            <div>
              <span className="text-text-muted" title="Last change to step data (not name or folder)">Last Modified</span>
              <p className="font-medium text-text-body">{updatedAt}</p>
            </div>
            <div>
              <span className="text-text-muted">Created</span>
              <p className="font-medium text-text-body">{new Date(project.created_at).toLocaleDateString('ko-KR')}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <div className="mr-auto flex items-center gap-2">
            {/* 2026-06-19 - 소프트 숨김/해제 (이슈 #7) */}
            <button
              type="button"
              onClick={() => setHidden(project.id, !project.is_hidden)}
              disabled={!canWrite}
              title={canWrite ? undefined : READ_ONLY_TITLE}
              className="rounded-md px-4 py-2 text-xs font-medium text-text-secondary disabled:opacity-40 transition-colors hover:bg-bg-input"
            >
              {project.is_hidden ? '🙈 Unhide' : '🙈 Hide'}
            </button>
            {/* 2026-07-09 - 소프트 삭제 (이슈 #35): 숨긴 상태에서만 노출(2단계 안전장치) */}
            {project.is_hidden && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canWrite}
                title={canWrite ? undefined : READ_ONLY_TITLE}
                className="rounded-md px-4 py-2 text-xs font-medium text-red-400 disabled:opacity-40 transition-colors hover:bg-red-500/10"
              >
                🗑 Delete
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-md px-4 py-2 text-xs text-text-secondary transition-colors hover:bg-bg-input"
          >
            Cancel
          </button>
          <button
            onClick={handleLoad}
            disabled={loading || isCurrentProject}
            className="rounded-md bg-accent-purple px-4 py-2 text-xs font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isCurrentProject ? 'Already Loaded' : loading ? 'Loading...' : 'Load Project'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
